/**
 * This file describes the status codes returned by the public timeline
 * operations.
 *
 * To make the code more readable, the pattern:
 *
 *    export const FOO = '....';
 *    export type FOO = typeof FOO;
 *
 * is used as a way to allow using the same identifier FOO both as
 * a return type and a return value.
 */

/**
 * When an action is successfully executed.
 */
export const OK = 'OK';
export type OK = typeof OK;

/**
 * When an action succeeds but the value to be used has been
 * clamped to the minimum valid value.
 */
export const WARN_CLAMPED_TO_MIN = 'clamped_to_min';
export type WARN_CLAMPED_TO_MIN = typeof WARN_CLAMPED_TO_MIN;

/**
 * When an action succeeds but the value to be used has been
 * clamped to the maximum valid value.
 */
export const WARN_CLAMPED_TO_MAX = 'clamped_to_max';
export type WARN_CLAMPED_TO_MAX = typeof WARN_CLAMPED_TO_MAX;

/**
 * When an action is ignored because there is an abortable
 * operation in progress.
 */
export const ERR_ABORTABLE_OP_IN_PROGRESS = 'op_in_progress';
export type ERR_ABORTABLE_OP_IN_PROGRESS = typeof ERR_ABORTABLE_OP_IN_PROGRESS;

/**
 * When an action is ignored because it provides an invalid in point
 * greater than the out point.
 */
export const ERR_IN_AFTER_OUT = 'in_after_out';
export type ERR_IN_AFTER_OUT = typeof ERR_IN_AFTER_OUT;

/**
 * When an action is ignored because it provides an invalid
 * end point past the maximum valid duration.
 */
export const ERR_OUT_PAST_DURATION = 'out_past_duration';
export type ERR_OUT_PAST_DURATION = typeof ERR_OUT_PAST_DURATION;

/**
 * When an action is ignored because it tries to do something
 * about an item that with different properties than what we
 * currently know about this item, like for instance trying
 * to use the same item ID with different item types (for example
 * trying to update the duration of an image Mimir item).
 */
export const ERR_ITEM_MISMATCH = 'item_mismatch';
export type ERR_ITEM_MISMATCH = typeof ERR_ITEM_MISMATCH;

/**
 * When an action is ignored because it tries to do something
 * about a box that is with different properties than what we
 * currently know about this box, like for instance trying
 * to perform an audio-only action with an image box.
 */
export const ERR_BOX_MISMATCH = 'box_mismatch';
export type ERR_BOX_MISMATCH = typeof ERR_BOX_MISMATCH;

/**
 * When an action is ignored because it tries to do something
 * about a box with an invalid box ID.
 */
export const ERR_UNKNOWN_BOX = 'unknown_box';
export type ERR_UNKNOWN_BOX = typeof ERR_UNKNOWN_BOX;

/**
 * When an action is ignored because it tries to operate
 * on an item that is unknown, like for instance trying
 * to update the duration of an item that is not part of
 * the timeline.
 */
export const ERR_UNKNOWN_ITEM = 'unknown_item';
export type ERR_UNKNOWN_ITEM = typeof ERR_UNKNOWN_ITEM;

/**
 * When an action is ignored because it expects a main track box
 * and is given a box from a different track.
 */
export const ERR_NOT_MAIN_TRACK_BOX = 'not_main_track_box';
export type ERR_NOT_MAIN_TRACK_BOX = typeof ERR_NOT_MAIN_TRACK_BOX;
